import { AmountDisplay, amountString } from "@/app/shared/ui/amount-display";
import { useDemoModeStore } from "@/store/demoMode";
import { zodResolver } from "@hookform/resolvers/zod";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { z } from "zod";

import { Badge } from "@groot/ui/badge";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Switch } from "@/app/shared/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@groot/ui/tabs";
import { useAccounts } from "@/hooks/api/useAccounts";
import {
  type ExtraPayment,
  type LoanInput,
  type LoanSummary,
  calculateInterestSavings,
  calculateLoanSummary,
  generateAmortizationSchedule,
} from "./lib/loanCalculator";

// `z.coerce.number<number>()` keeps the coercion but types the input as a
// number, so the form's field values and the resolver agree on one shape.
const loanFormSchema = z.object({
  accountId: z.string().optional(),
  principal: z.coerce.number<number>().positive("Principal must be positive"),
  annualInterestRate: z.coerce
    .number<number>()
    .min(0, "Interest rate must be 0 or greater")
    .max(100, "Interest rate must be less than 100"),
  tenureMonths: z.coerce.number<number>().int().positive("Tenure must be positive"),
  emiAmount: z.coerce.number<number>().optional(),
  startDate: z.string().optional(),
});

type LoanFormData = z.infer<typeof loanFormSchema>;

export function LoanCalculator() {
  const { enabled: isDemoMode } = useDemoModeStore();
  const { data: accounts } = useAccounts();
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [extraPayments, setExtraPayments] = useState<ExtraPayment[]>([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [extraPaymentForm, setExtraPaymentForm] = useState({
    month: "",
    amount: "",
  });
  const [monthInputType, setMonthInputType] = useState<"number" | "date">("number");

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<LoanFormData>({
    resolver: zodResolver(loanFormSchema),
    defaultValues: {
      principal: 100000,
      annualInterestRate: 10,
      tenureMonths: 12,
      startDate: new Date().toISOString().split("T")[0],
    },
  });

  // Get all loan accounts
  const allLoanAccounts = useMemo(() => {
    return accounts?.filter((account) => account.type === "loan") || [];
  }, [accounts]);

  // Filter accounts based on showAllAccounts toggle
  const displayedLoanAccounts = useMemo(() => {
    if (showAllAccounts) return allLoanAccounts;
    return allLoanAccounts.filter((account) => !account.isArchived && !account.isDeleted);
  }, [allLoanAccounts, showAllAccounts]);

  // Watch form values for real-time calculation
  const formValues = watch();

  // Handle account selection
  const handleAccountSelect = (accountId: string) => {
    if (accountId === "manual") {
      reset({
        principal: 100000,
        annualInterestRate: 10,
        tenureMonths: 12,
        startDate: new Date().toISOString().split("T")[0],
      });
      return;
    }

    const account = displayedLoanAccounts.find((acc) => acc.id === Number(accountId));
    if (account) {
      setValue("accountId", accountId);

      // Determine Principal: Prioritize meta.loanAmount, then fallback to currentBalance
      const metaLoanAmount = account.meta?.loanAmount;
      const principal =
        typeof metaLoanAmount === "number" && metaLoanAmount > 0
          ? metaLoanAmount
          : Math.abs(account.currentBalance);
      setValue("principal", principal);

      setValue("annualInterestRate", account.interestRate || 10);
      setValue("tenureMonths", account.termInMonths || 12);

      // Determine Start Date: Prioritize meta.paymentStartDate, then top-level paymentStartDate
      const metaStartDate = account.meta?.paymentStartDate;
      const startDate =
        typeof metaStartDate === "string" && metaStartDate
          ? metaStartDate
          : account.paymentStartDate || new Date().toISOString().split("T")[0];

      setValue("startDate", startDate);
    }
  };

  // Calculate loan details
  const loanInput: LoanInput = useMemo(() => {
    return {
      principal: formValues.principal || 0,
      annualInterestRate: formValues.annualInterestRate || 0,
      tenureMonths: formValues.tenureMonths || 0,
      emiAmount: formValues.emiAmount,
    };
  }, [formValues]);

  const schedule = useMemo(() => {
    if (!loanInput.principal || !loanInput.tenureMonths) return [];
    return generateAmortizationSchedule(
      loanInput,
      extraPayments,
      formValues.startDate ? new Date(formValues.startDate) : new Date(),
    );
  }, [loanInput, extraPayments, formValues.startDate]);

  const summary: LoanSummary | null = useMemo(() => {
    if (!loanInput.principal || !loanInput.tenureMonths) return null;
    return calculateLoanSummary(
      loanInput,
      extraPayments,
      formValues.startDate ? new Date(formValues.startDate) : new Date(),
    );
  }, [loanInput, extraPayments, formValues.startDate]);

  const savingsInfo = useMemo(() => {
    if (!loanInput.principal || !loanInput.tenureMonths || extraPayments.length === 0) {
      return null;
    }
    return calculateInterestSavings(loanInput, extraPayments);
  }, [loanInput, extraPayments]);

  // Chart data
  const chartData = useMemo(() => {
    return schedule.map((payment) => ({
      month: payment.month,
      principal: payment.principalAmount,
      interest: payment.interestAmount,
      balance: payment.remainingBalance,
      extraPayment: payment.extraPayment,
    }));
  }, [schedule]);

  // Convert YYYY-MM date to month number based on start date
  const dateToMonthNumber = (dateStr: string, startDate: string): number | null => {
    const paymentDate = new Date(`${dateStr}-01`);
    const loanStartDate = new Date(`${startDate}-01`);

    if (Number.isNaN(paymentDate.getTime()) || Number.isNaN(loanStartDate.getTime())) {
      return null;
    }

    const monthsDiff =
      (paymentDate.getFullYear() - loanStartDate.getFullYear()) * 12 +
      (paymentDate.getMonth() - loanStartDate.getMonth());

    return monthsDiff + 1; // Month 1 is the first payment
  };

  // Add extra payment
  const handleAddExtraPayment = () => {
    const amount = Number(extraPaymentForm.amount);

    if (amount <= 0 || !formValues.startDate) {
      return;
    }

    let month: number | null = null;

    // Determine month number based on input type
    if (monthInputType === "number") {
      month = Number(extraPaymentForm.month);
      if (month <= 0 || Number.isNaN(month)) {
        return;
      }
    } else {
      // Convert YYYY-MM to month number
      month = dateToMonthNumber(extraPaymentForm.month, formValues.startDate);
      if (!month || month <= 0) {
        return;
      }
    }

    // Validate month is within tenure
    if (month > (summary?.actualTenure || formValues.tenureMonths)) {
      return;
    }

    setExtraPayments((prev) => [...prev, { month, amount }].sort((a, b) => a.month - b.month));
    setExtraPaymentForm({ month: "", amount: "" });
  };

  // Remove extra payment
  const handleRemoveExtraPayment = (index: number) => {
    setExtraPayments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Loan Repayment Calculator"
        description="Calculate your loan EMI, view amortization schedule, and plan extra payments"
        className="mb-8"
      />

      <div className="space-y-6">
        {/* Input Form */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="border-b border-border bg-muted px-6 py-4">
            <h2 className="text-base font-medium text-foreground">Loan Details</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Select an existing loan account or enter details manually
            </p>
          </div>
          <div className="p-6">
            <form className="space-y-4">
              {/* Account Selection */}
              {allLoanAccounts.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select Loan Account (Optional)</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="show-all-accounts"
                        checked={showAllAccounts}
                        onCheckedChange={setShowAllAccounts}
                      />
                      <Label
                        htmlFor="show-all-accounts"
                        className="text-xs text-muted-foreground font-normal cursor-pointer"
                      >
                        Show all
                      </Label>
                    </div>
                  </div>
                  <Select onValueChange={handleAccountSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a loan account or enter manually" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                      {displayedLoanAccounts.map((account) => (
                        <SelectItem key={account.id} value={String(account.id)}>
                          {account.accountName} -{" "}
                          {amountString(Math.abs(account.currentBalance), "INR", isDemoMode)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Principal */}
                <div className="space-y-2">
                  <Label htmlFor="principal">Loan Amount (Principal)</Label>
                  <Input id="principal" type="number" step="1000" {...register("principal")} />
                  {errors.principal && (
                    <p className="text-sm text-destructive">{errors.principal.message}</p>
                  )}
                </div>

                {/* Interest Rate */}
                <div className="space-y-2">
                  <Label htmlFor="annualInterestRate">Annual Interest Rate (%)</Label>
                  <Input
                    id="annualInterestRate"
                    type="number"
                    step="0.1"
                    {...register("annualInterestRate")}
                  />
                  {errors.annualInterestRate && (
                    <p className="text-sm text-destructive">{errors.annualInterestRate.message}</p>
                  )}
                </div>

                {/* Tenure */}
                <div className="space-y-2">
                  <Label htmlFor="tenureMonths">Loan Tenure (Months)</Label>
                  <Input id="tenureMonths" type="number" step="1" {...register("tenureMonths")} />
                  {errors.tenureMonths && (
                    <p className="text-sm text-destructive">{errors.tenureMonths.message}</p>
                  )}
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label htmlFor="startDate">Payment Start Date</Label>
                  <Input id="startDate" type="date" {...register("startDate")} />
                </div>

                {/* Optional EMI Override */}
                <div className="space-y-2">
                  <Label htmlFor="emiAmount">Custom EMI Amount (Optional)</Label>
                  <Input
                    id="emiAmount"
                    type="number"
                    step="100"
                    placeholder="Leave empty to auto-calculate"
                    {...register("emiAmount")}
                  />
                  <p className="text-xs text-muted-foreground">Override calculated EMI if needed</p>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-border bg-background p-4">
              <p className="text-sm text-muted-foreground">Monthly EMI</p>
              <div className="text-2xl font-medium text-foreground mt-1">
                <AmountDisplay amount={summary.monthlyEMI} />
              </div>
            </div>

            <div className="rounded-lg border border-border bg-background p-4">
              <p className="text-sm text-muted-foreground">Total Interest</p>
              <div className="text-2xl font-medium text-destructive mt-1">
                <AmountDisplay amount={summary.totalInterest} />
              </div>
              {savingsInfo && savingsInfo.savedInterest > 0 && (
                <p className="text-xs text-success mt-1">
                  Saving <AmountDisplay amount={savingsInfo.savedInterest} />
                </p>
              )}
            </div>

            <div className="rounded-lg border border-border bg-background p-4">
              <p className="text-sm text-muted-foreground">Total Payment</p>
              <div className="text-2xl font-medium text-foreground mt-1">
                <AmountDisplay amount={summary.totalPayment} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Principal + Interest{" "}
                {summary.totalExtraPayments > 0 &&
                  `+ Extra ${amountString(summary.totalExtraPayments, "INR", isDemoMode)}`}
              </p>
            </div>

            <div className="rounded-lg border border-border bg-background p-4">
              <p className="text-sm text-muted-foreground">Debt Free Date</p>
              <div className="text-2xl font-medium text-success mt-1">
                {new Date(summary.debtFreeDate).toLocaleDateString("en-IN", {
                  month: "short",
                  year: "numeric",
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {summary.actualTenure} months
                {savingsInfo && savingsInfo.monthsSaved > 0 && (
                  <span className="text-success"> ({savingsInfo.monthsSaved} months saved)</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Extra Payments Section */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="border-b border-border bg-muted px-6 py-4">
            <h2 className="text-base font-medium text-foreground">Extra Payments</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add extra payments to see how they reduce your loan tenure and interest
            </p>
          </div>
          <div className="p-6 space-y-4">
            <Tabs
              value={monthInputType}
              onValueChange={(value) => {
                setMonthInputType(value as "number" | "date");
                setExtraPaymentForm({ month: "", amount: "" });
              }}
              className="w-full"
            >
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="number">By Month Number</TabsTrigger>
                <TabsTrigger value="date">By Date (YYYY-MM)</TabsTrigger>
              </TabsList>

              <TabsContent value="number" className="mt-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="extraMonthNumber">Month Number</Label>
                    <Input
                      id="extraMonthNumber"
                      type="number"
                      placeholder="e.g., 1, 2, 3..."
                      value={extraPaymentForm.month}
                      onChange={(e) =>
                        setExtraPaymentForm((prev) => ({
                          ...prev,
                          month: e.target.value,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter the payment month (1 = first month, 2 = second month, etc.)
                    </p>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="extraAmountNumber">Amount</Label>
                    <Input
                      id="extraAmountNumber"
                      type="number"
                      placeholder="Extra payment amount"
                      value={extraPaymentForm.amount}
                      onChange={(e) =>
                        setExtraPaymentForm((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddExtraPayment} size="sm">
                      <Plus className="size-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="date" className="mt-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="extraMonthDate">Payment Month (YYYY-MM)</Label>
                    <Input
                      id="extraMonthDate"
                      type="month"
                      placeholder="2025-01"
                      value={extraPaymentForm.month}
                      onChange={(e) =>
                        setExtraPaymentForm((prev) => ({
                          ...prev,
                          month: e.target.value,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Select the month when you'll make the extra payment
                    </p>
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="extraAmountDate">Amount</Label>
                    <Input
                      id="extraAmountDate"
                      type="number"
                      placeholder="Extra payment amount"
                      value={extraPaymentForm.amount}
                      onChange={(e) =>
                        setExtraPaymentForm((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddExtraPayment} size="sm">
                      <Plus className="size-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {extraPayments.length > 0 && (
              <div className="space-y-2">
                <Label>Added Extra Payments</Label>
                <div className="flex flex-wrap gap-2">
                  {extraPayments.map((payment, index) => {
                    // Calculate the date for this payment based on month number
                    const startDate = formValues.startDate
                      ? new Date(formValues.startDate)
                      : new Date();
                    const paymentDate = new Date(startDate);
                    paymentDate.setMonth(startDate.getMonth() + payment.month - 1);
                    const dateStr = paymentDate.toLocaleDateString("en-IN", {
                      month: "short",
                      year: "numeric",
                    });

                    return (
                      <Badge
                        // biome-ignore lint/suspicious/noArrayIndexKey: index needed for deletion
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-2"
                      >
                        Month {payment.month} ({dateStr}): <AmountDisplay amount={payment.amount} />
                        <button
                          type="button"
                          onClick={() => handleRemoveExtraPayment(index)}
                          className="ml-1 hover:text-destructive"
                        >
                          <Trash2 className="size-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Charts */}
        {schedule.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Principal vs Interest Chart */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground">
                  Principal vs Interest Breakdown
                </h2>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => amountString(value, "INR", isDemoMode)}
                      labelFormatter={(label) => `Month ${label}`}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="principal"
                      stackId="1"
                      stroke="#10b981"
                      fill="#10b981"
                      name="Principal"
                    />
                    <Area
                      type="monotone"
                      dataKey="interest"
                      stackId="1"
                      stroke="#ef4444"
                      fill="#ef4444"
                      name="Interest"
                    />
                    {extraPayments.length > 0 && (
                      <Area
                        type="monotone"
                        dataKey="extraPayment"
                        stackId="1"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        name="Extra Payment"
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Outstanding Balance Chart */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground">
                  Outstanding Balance Over Time
                </h2>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => amountString(value, "INR", isDemoMode)}
                      labelFormatter={(label) => `Month ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      name="Balance"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Amortization Schedule Table */}
        {schedule.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-medium text-foreground">Amortization Schedule</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Detailed month-by-month payment breakdown
                  </p>
                </div>
                <Button variant="outline" onClick={() => setShowSchedule(!showSchedule)}>
                  {showSchedule ? "Hide" : "Show"} Schedule
                </Button>
              </div>
            </div>
            {showSchedule && (
              <div className="p-6">
                <div className="rounded-md border border-border overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted hover:bg-muted">
                        <TableHead className="w-20 font-medium text-foreground/80">Month</TableHead>
                        <TableHead className="font-medium text-foreground/80">Date</TableHead>
                        <TableHead className="text-right font-medium text-foreground/80">
                          EMI
                        </TableHead>
                        <TableHead className="text-right font-medium text-foreground/80">
                          Principal
                        </TableHead>
                        <TableHead className="text-right font-medium text-foreground/80">
                          Interest
                        </TableHead>
                        <TableHead className="text-right font-medium text-foreground/80">
                          Extra Payment
                        </TableHead>
                        <TableHead className="text-right font-medium text-foreground/80">
                          Total Payment
                        </TableHead>
                        <TableHead className="text-right font-medium text-foreground/80">
                          Balance
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedule.map((payment) => (
                        <TableRow key={payment.month}>
                          <TableCell className="font-medium">{payment.month}</TableCell>
                          <TableCell>
                            {payment.date &&
                              new Date(payment.date).toLocaleDateString("en-IN", {
                                month: "short",
                                year: "numeric",
                              })}
                          </TableCell>
                          <TableCell className="text-right">
                            <AmountDisplay amount={payment.emiAmount} />
                          </TableCell>
                          <TableCell className="text-right text-success">
                            <AmountDisplay amount={payment.principalAmount} />
                          </TableCell>
                          <TableCell className="text-right text-destructive">
                            <AmountDisplay amount={payment.interestAmount} />
                          </TableCell>
                          <TableCell className="text-right text-info">
                            {payment.extraPayment > 0 ? (
                              <AmountDisplay amount={payment.extraPayment} />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            <AmountDisplay amount={payment.totalPayment} />
                          </TableCell>
                          <TableCell className="text-right">
                            <AmountDisplay amount={payment.remainingBalance} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}
